import { useState, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { colors, formatNumber } from '../styles/tokens'
import { Button } from './FormControls'
import { exportBoqToExcel, downloadImportTemplate } from '../lib/excelExport'
import { exportTenderSummaryPdf, exportBoqDetailPdf } from '../lib/pdfExport'
import { parseImportFile, validateImportRows } from '../lib/excelImport'

// =======================================================================
// ImportExportPanel — Wave 4.5 action bar + import preview/confirm flow.
// All financial figures shown come from the existing calculation chain;
// this component writes only boq_item master fields (item_no,
// description, unit, resolved_quantity) — it never touches
// rate_buildup_line, first_principles_detail, crew, productivity, or
// resource_rate.
// =======================================================================

const STATUS_STYLE = {
  valid: { bg: '#F0FDF4', border: '#86EFAC', text: '#166534', label: 'Valid' },
  warning: { bg: '#FFF7ED', border: '#FDBA74', text: '#9A3412', label: 'Warning' },
  error: { bg: '#FEF2F2', border: '#FCA5A5', text: '#991B1B', label: 'Error' },
  ambiguous: { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E', label: 'Ambiguous' },
}

export default function ImportExportPanel({ tenderId, revisionId, isFrozen, onImported }) {
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  const [preview, setPreview] = useState(null) // { rows: [...] }
  const [filter, setFilter] = useState('all')
  const [importing, setImporting] = useState(false)
  const fileInputRef = useRef(null)

  async function handleExportExcel() {
    setBusy('export-excel'); setError(null)
    try { await exportBoqToExcel(tenderId, revisionId); setSuccessMsg('BOQ exported to Excel.') }
    catch (e) { setError(`Export failed: ${e.message}`) }
    setBusy(null)
  }

  async function handleDownloadTemplate() {
    setBusy('template'); setError(null)
    try { await downloadImportTemplate(); setSuccessMsg('Import template downloaded.') }
    catch (e) { setError(`Could not generate template: ${e.message}`) }
    setBusy(null)
  }

  async function handleExportTenderPdf() {
    setBusy('pdf-tender'); setError(null)
    try { await exportTenderSummaryPdf(tenderId, revisionId) }
    catch (e) { setError(`PDF generation failed: ${e.message}`) }
    setBusy(null)
  }

  async function handleExportBoqPdf() {
    setBusy('pdf-boq'); setError(null)
    try { await exportBoqDetailPdf(tenderId, revisionId) }
    catch (e) { setError(`PDF generation failed: ${e.message}`) }
    setBusy(null)
  }

  async function handleFileSelected(e) {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file later
    if (!file) return
    setBusy('parsing'); setError(null); setSuccessMsg(null)
    try {
      const parsedRows = await parseImportFile(file)
      if (parsedRows.length === 0) throw new Error('The file contains no data rows.')

      // Fetch existing BOQ items in this revision (by item_no) so the
      // validator can classify Create vs Update, and flag pricing
      // protection warnings using the existing pricing-status logic.
      const { data: existingItems, error: existingErr } = await supabase
        .from('boq_item_pricing_status')
        .select('boq_item_id, item_no, line_count')
        .eq('estimate_revision_id', revisionId)
      if (existingErr) throw new Error(existingErr.message)

      const existingByNo = {}
      for (const it of existingItems || []) {
        if (it.item_no) existingByNo[it.item_no] = { boqItemId: it.boq_item_id, hasPricing: Number(it.line_count) > 0 }
      }

      const validated = validateImportRows(parsedRows, existingByNo)
      setPreview({ rows: validated, existingByNo })
    } catch (e) {
      setError(`Could not read file: ${e.message}`)
    }
    setBusy(null)
  }

  async function handleConfirmImport() {
    if (!preview) return
    const actionable = preview.rows.filter((r) => r.status === 'valid' || r.status === 'warning')
    if (actionable.length === 0) return

    setImporting(true); setError(null)
    try {
      const toCreate = actionable.filter((r) => r.action === 'create')
      const toUpdate = actionable.filter((r) => r.action === 'update')

      if (toCreate.length > 0) {
        const { error: insertErr } = await supabase.from('boq_item').insert(
          toCreate.map((r) => ({
            estimate_revision_id: revisionId,
            item_no: r.itemNo,
            description: r.description,
            unit: r.unit,
            resolved_quantity: r.quantity,
          }))
        )
        if (insertErr) throw new Error(insertErr.message)
      }

      for (const r of toUpdate) {
        const existing = preview.existingByNo[r.itemNo]
        const { error: updateErr } = await supabase
          .from('boq_item')
          .update({ description: r.description, unit: r.unit, resolved_quantity: r.quantity })
          .eq('id', existing.boqItemId)
        if (updateErr) throw new Error(updateErr.message)
      }

      setSuccessMsg(`Import complete — ${toCreate.length} created, ${toUpdate.length} updated.`)
      setPreview(null)
      onImported?.()
    } catch (e) {
      setError(`Import failed: ${e.message}`)
    }
    setImporting(false)
  }

  const counts = preview ? preview.rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc }, {}) : null
  const filteredRows = preview ? (filter === 'all' ? preview.rows : preview.rows.filter((r) => r.status === filter)) : []
  const hasErrors = counts?.error > 0

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button onClick={handleExportExcel} disabled={busy === 'export-excel'} size="sm">
          {busy === 'export-excel' ? 'Exporting…' : '📊 Export Excel'}
        </Button>
        <Button variant="secondary" onClick={handleDownloadTemplate} disabled={busy === 'template'} size="sm">
          {busy === 'template' ? 'Preparing…' : '⬇ Download Template'}
        </Button>
        <Button
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={isFrozen || busy === 'parsing'}
          size="sm"
          title={isFrozen ? 'This revision is frozen and cannot be modified.' : undefined}
        >
          {busy === 'parsing' ? 'Reading…' : '📥 Import from Excel'}
        </Button>
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileSelected} style={{ display: 'none' }} />
        <div style={{ width: 1, height: 20, background: colors.border, margin: '0 4px' }} />
        <Button variant="secondary" onClick={handleExportTenderPdf} disabled={busy === 'pdf-tender'} size="sm">
          {busy === 'pdf-tender' ? 'Generating…' : '📄 Tender PDF'}
        </Button>
        <Button variant="secondary" onClick={handleExportBoqPdf} disabled={busy === 'pdf-boq'} size="sm">
          {busy === 'pdf-boq' ? 'Generating…' : '📄 BOQ PDF'}
        </Button>
      </div>

      {isFrozen && (
        <div style={{ marginTop: 8, padding: '8px 12px', background: colors.warningBg, color: colors.warningText, borderRadius: 6, fontSize: 12 }}>
          This revision is frozen and cannot be modified. Import is disabled — export and PDF actions remain available.
        </div>
      )}

      {error && (
        <div style={{ marginTop: 8, padding: '8px 12px', background: colors.dangerBg, color: colors.danger, borderRadius: 6, fontSize: 12 }}>
          ⚠ {error}
        </div>
      )}
      {successMsg && !preview && (
        <div style={{ marginTop: 8, padding: '8px 12px', background: colors.successBg, color: colors.successText, borderRadius: 6, fontSize: 12 }}>
          ✓ {successMsg}
        </div>
      )}

      {/* ===================== IMPORT PREVIEW PANEL ===================== */}
      {preview && (
        <div style={{ marginTop: 14, border: `2px solid ${colors.primary}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px', background: colors.primary, color: '#fff', fontSize: 13, fontWeight: 700 }}>
            IMPORT PREVIEW — {preview.rows.length} row(s) found
          </div>

          <div style={{ padding: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <SummaryChip label="Total Rows" value={preview.rows.length} />
            <SummaryChip label="Valid" value={counts.valid || 0} cfg={STATUS_STYLE.valid} />
            <SummaryChip label="Warnings" value={counts.warning || 0} cfg={STATUS_STYLE.warning} />
            <SummaryChip label="Errors" value={counts.error || 0} cfg={STATUS_STYLE.error} />
            <SummaryChip label="Ambiguous" value={counts.ambiguous || 0} cfg={STATUS_STYLE.ambiguous} />
          </div>

          <div style={{ padding: '0 14px', display: 'flex', gap: 6, marginBottom: 10 }}>
            {['all', 'valid', 'warning', 'error', 'ambiguous'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${filter === f ? colors.primary : colors.border}`,
                  background: filter === f ? '#EEF2FF' : '#fff', color: filter === f ? colors.primary : colors.textSecondary,
                }}
              >
                {f === 'all' ? 'All' : STATUS_STYLE[f].label}
              </button>
            ))}
          </div>

          <div style={{ maxHeight: 320, overflowY: 'auto', padding: '0 14px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.border}`, position: 'sticky', top: 0, background: '#fff' }}>
                  <Th>Row</Th><Th>Item No.</Th><Th>Description</Th><Th>Unit</Th><Th align="right">Qty</Th><Th>Action</Th><Th>Status</Th><Th>Issue</Th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const cfg = STATUS_STYLE[r.status]
                  return (
                    <tr key={r.rowNumber} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <Td muted>{r.rowNumber}</Td>
                      <Td>{r.itemNo || '—'}</Td>
                      <Td>{r.description || '—'}</Td>
                      <Td muted>{r.unit || '—'}</Td>
                      <Td align="right">{r.quantity != null && r.quantity !== '' ? formatNumber(r.quantity) : '—'}</Td>
                      <Td muted style={{ textTransform: 'capitalize' }}>{r.action || '—'}</Td>
                      <Td>
                        <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 10, background: cfg.bg, border: `1px solid ${cfg.border}`, fontSize: 10, fontWeight: 700, color: cfg.text }}>
                          {cfg.label}
                        </span>
                      </Td>
                      <Td style={{ fontSize: 11, color: colors.textMuted }}>{r.issues.join(' ')}</Td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div style={{ padding: 14, borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: colors.textSecondary }}>
              {(counts.valid || 0) + (counts.warning || 0)} item(s) will be imported
              {hasErrors && <span style={{ color: colors.danger, fontWeight: 700 }}> — resolve {counts.error} error(s) first</span>}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="secondary" onClick={() => setPreview(null)}>Cancel</Button>
              <Button onClick={handleConfirmImport} disabled={hasErrors || importing || ((counts.valid || 0) + (counts.warning || 0) === 0)}>
                {importing ? 'Importing…' : 'Confirm Import'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function SummaryChip({ label, value, cfg }) {
  return (
    <div style={{
      padding: '6px 12px', borderRadius: 8, background: cfg?.bg || '#F9FAFB', border: `1px solid ${cfg?.border || colors.border}`,
    }}>
      <div style={{ fontSize: 10, color: cfg?.text || colors.textMuted, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: cfg?.text || colors.textPrimary }}>{value}</div>
    </div>
  )
}

function Th({ children, align = 'left' }) {
  return <th style={{ textAlign: align, padding: '6px 8px', fontSize: 10, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>{children}</th>
}
function Td({ children, align = 'left', muted, style }) {
  return <td style={{ textAlign: align, padding: '7px 8px', color: muted ? colors.textMuted : colors.textPrimary, ...style }}>{children}</td>
}
